import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '@/lib/api';
import type { ProductDTO } from '@/lib/api';

export type CartLine = {
  productId: number;
  name: string;
  priceCents: number;
  imageUrl: string | null;
  quantity: number;
};

type CartState = {
  lines: CartLine[];
  add: (product: ProductDTO, quantity?: number) => void;
  setQuantity: (productId: number, quantity: number) => void;
  remove: (productId: number) => void;
  clear: () => void;
  totalCents: () => number;
  totalQuantity: () => number;
};

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],
      add: (product, quantity = 1) =>
        set((state) => {
          const existing = state.lines.find((l) => l.productId === product.id);
          if (existing) {
            return {
              lines: state.lines.map((l) =>
                l.productId === product.id
                  ? { ...l, quantity: l.quantity + quantity }
                  : l,
              ),
            };
          }
          return {
            lines: [
              ...state.lines,
              {
                productId: product.id,
                name: product.name,
                priceCents: product.priceCents,
                imageUrl: product.imageUrl,
                quantity,
              },
            ],
          };
        }),
      setQuantity: (productId, quantity) =>
        set((state) => ({
          lines:
            quantity <= 0
              ? state.lines.filter((l) => l.productId !== productId)
              : state.lines.map((l) =>
                  l.productId === productId ? { ...l, quantity } : l,
                ),
        })),
      remove: (productId) =>
        set((state) => ({
          lines: state.lines.filter((l) => l.productId !== productId),
        })),
      clear: () => set({ lines: [] }),
      totalCents: () =>
        get().lines.reduce((sum, l) => sum + l.priceCents * l.quantity, 0),
      totalQuantity: () => get().lines.reduce((sum, l) => sum + l.quantity, 0),
    }),
    { name: 'cart-v1' },
  ),
);

type AuthUser = { id: number; email: string; name: string | null };

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

export const useAuth = create<AuthState>((set) => ({
  user: null,
  loading: false,
  refresh: async () => {
    set({ loading: true });
    try {
      const user = await api.auth.me();
      set({ user, loading: false });
    } catch {
      set({ user: null, loading: false });
    }
  },
  login: async (email, password) => {
    const user = await api.auth.login(email, password);
    set({ user });
  },
  logout: async () => {
    await api.auth.logout();
    set({ user: null });
  },
}));
